import React, { useState } from 'react';
import {
  ArrowRight, ShieldCheck, Eye, ClipboardList, Layers, Settings2,
  LineChart, Lock, EyeOff, UserCheck, Sparkles, ChevronLeft,
} from 'lucide-react';
import PreventiAppLogo from '../../components/PreventiAppLogo';

/**
 * Onboarding de 3 pantallas, solo en el primer uso (lo controla MobileApp.jsx).
 * Slides:
 *   1. Bienvenido a PreventiApp
 *   2. Cómo registrar una observación
 *   3. Privacidad y uso responsable
 */
const SLIDES = [
  {
    id: 'bienvenido',
    badge: 'Bienvenido',
    title: 'Bienvenido a PreventiApp',
    subtitle: 'Inteligencia preventiva escolar para fortalecer la convivencia con observaciones rápidas, trazables y útiles.',
    visual: 'welcome',
  },
  {
    id: 'como',
    badge: 'Cómo registrar',
    title: 'Cómo registrar una observación',
    subtitle: 'Cinco pasos guiados, pensados para terreno. Sin formularios largos.',
    visual: 'steps',
  },
  {
    id: 'privacidad',
    badge: 'Privacidad y ética',
    title: 'Privacidad y uso responsable',
    subtitle: 'PreventiApp es una herramienta preventiva, no diagnóstica. Lo que registras se trata con cuidado.',
    visual: 'privacy',
  },
];

const WelcomeVisual = () => (
  <div className="relative h-44 mx-auto flex items-center justify-center">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-44 h-44 rounded-full bg-[#EEEAFE] opacity-70 blur-xl" />
    </div>
    <PreventiAppLogo className="relative h-14 w-auto" variant="mark" />
  </div>
);

const StepsVisual = () => {
  const steps = [
    { icon: Eye, label: 'Observar', tone: { bg: '#FFE0DA', fg: '#E25E48' } },
    { icon: ClipboardList, label: 'Registrar', tone: { bg: '#EEEAFE', fg: '#5346E6' } },
    { icon: Layers, label: 'Clasificar', tone: { bg: '#FFF1D6', fg: '#C98718' } },
    { icon: Settings2, label: 'Gestionar', tone: { bg: '#FCE4EF', fg: '#D24B8A' } },
    { icon: LineChart, label: 'Prevenir', tone: { bg: '#DFF6EC', fg: '#1E9F73' } },
  ];
  return (
    <div className="grid grid-cols-5 gap-2 px-2">
      {steps.map((s) => (
        <div key={s.label} className="flex flex-col items-center gap-2">
          <span
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: s.tone.bg, color: s.tone.fg }}
          >
            <s.icon className="w-5 h-5" />
          </span>
          <span className="text-[10px] font-bold text-brand-deep leading-tight text-center">
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
};

const PrivacyVisual = () => {
  const items = [
    { icon: UserCheck, text: 'Usa lenguaje observable, sin diagnósticos.' },
    { icon: EyeOff, text: 'Resguarda identidades en evidencia (fotos sin rostros).' },
    { icon: Lock, text: 'Acceso restringido al equipo autorizado.' },
  ];
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.text} className="rounded-xl bg-[#EEF4FF] border border-[#DDE6F7] p-3 flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-white text-brand-violet flex items-center justify-center shrink-0 border border-[#DDE6F7]">
            <it.icon className="w-4 h-4" />
          </span>
          <p className="text-[13px] text-brand-deep font-semibold leading-snug">{it.text}</p>
        </div>
      ))}
    </div>
  );
};

const ScreenOnboarding = ({ onComplete }) => {
  const [idx, setIdx] = useState(0);
  const isLast = idx === SLIDES.length - 1;
  const slide = SLIDES[idx];

  const next = () => {
    if (isLast) onComplete();
    else setIdx((i) => i + 1);
  };
  const prev = () => idx > 0 && setIdx((i) => i - 1);

  return (
    <div className="step-enter flex flex-col h-full" data-testid={`onboarding-slide-${slide.id}`}>
      {/* top: skip + back */}
      <div className="px-5 pt-3 pb-2 flex items-center justify-between">
        <button
          onClick={prev}
          data-testid="onboarding-back"
          aria-label="Atrás"
          disabled={idx === 0}
          className="tap-44 w-11 h-11 rounded-full bg-white border border-[#DDE6F7] flex items-center justify-center text-brand-deep disabled:opacity-30"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          data-testid="onboarding-skip"
          onClick={onComplete}
          className="text-sm font-bold text-brand-muted hover:text-brand-violet transition-colors"
        >
          Saltar
        </button>
      </div>

      {/* body */}
      <div className="flex-1 px-6 pt-6 flex flex-col">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EEEAFE] text-brand-violet text-xs font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            {slide.badge}
          </div>
          <h2 className="mt-5 text-[26px] font-extrabold text-brand-deep leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {slide.title}
          </h2>
          <p className="mt-3 text-[15px] text-brand-muted leading-relaxed">
            {slide.subtitle}
          </p>

          <div className="mt-8">
            {slide.visual === 'welcome' && <WelcomeVisual />}
            {slide.visual === 'steps' && <StepsVisual />}
            {slide.visual === 'privacy' && <PrivacyVisual />}
          </div>
        </div>
      </div>

      {/* footer: dots + button */}
      <div className="px-6 pb-7 pt-3">
        <div className="flex items-center justify-center gap-2 mb-5" data-testid="onboarding-dots">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-brand-violet' : 'w-2 bg-[#DDE6F7]'}`}
            />
          ))}
        </div>
        <button data-testid="onboarding-next" onClick={next} className="m-btn-primary">
          {isLast ? (
            <>
              Comenzar
              <ShieldCheck className="w-5 h-5" />
            </>
          ) : (
            <>
              Siguiente
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ScreenOnboarding;
